import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Box, Button, Container, Grid, Typography, useMediaQuery, Stack } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { RouteNames } from '../../constants/RouteNames';
import { useLocalActor } from '@api/useLocalActor';

// Icons
import BrushIcon from '@mui/icons-material/Brush';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
// Using a generic icon for customizable until a better one is chosen
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';

export const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const actor = useLocalActor();
    const { t } = useTranslation();

    const handleCreateRoom = () => {
        navigate('/dashboard');
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            backgroundColor: theme.palette.brandColors.lavender.wash,
            display: 'flex',
            flexDirection: 'column',
        }}>
            <Container maxWidth="lg">
                <Box sx={{
                    padding: theme.spacing(4, 0),
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                     {/* Logo Image */}
                    <Box 
                        component="img"
                        src="/logo_ineo_space.png"
                        alt="IneoSpace"
                        sx={{
                            height: 50,
                            width: 'auto',
                        }}
                    />

                    {/* Top Right Auth */}
                    {(!actor || actor.kind?.includes('guest')) && (
                        <Button
                            onClick={() => navigate(RouteNames.LOGIN)}
                            variant="text"
                            sx={{
                                color: theme.palette.brandColors.vintageInk.regular,
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: '1rem',
                                '&:hover': {
                                    background: 'transparent',
                                    color: theme.palette.brandColors.blueberry.bold,
                                }
                            }}
                        >
                            {t('header.signin')}
                        </Button>
                    )}
                </Box>
            </Container>

            {/* Hero Section */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                py: { xs: 6, md: 10 },
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6} sx={{ zIndex: 2 }}>
                            <Typography variant="h1" sx={{
                                fontWeight: 800,
                                mb: 3,
                                lineHeight: 1.1,
                                color: theme.palette.text.primary,
                                fontSize: { xs: '2.5rem', md: '4rem' },
                            }}>
                                <Trans
                                    i18nKey="pages.landingPage.hero.title"
                                    components={{
                                        highlight: <span style={{ color: theme.palette.brandColors.blueberry.bold }} />
                                    }}
                                />
                            </Typography>
                            <Typography variant="body1" sx={{
                                fontSize: '1.25rem',
                                color: theme.palette.text.secondary,
                                mb: 5,
                                maxWidth: 600,
                                mx: { xs: 'auto', md: 0 },
                                textAlign: { xs: 'center', md: 'left' }
                            }}>
                                {t('pages.landingPage.hero.subtitle')}
                            </Typography>
                            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                                <Button
                                    onClick={handleCreateRoom}
                                    variant="contained"
                                    disableElevation
                                    sx={{
                                        padding: theme.spacing(1.5, 5),
                                        fontSize: '1.1rem',
                                        borderRadius: 50,
                                        boxShadow: `0 8px 20px -4px ${theme.palette.brandColors.blueberry.regular}`,
                                        background: `linear-gradient(135deg, ${theme.palette.brandColors.lavender.regular}, ${theme.palette.brandColors.blueberry.light})`,
                                        color: 'white',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: `0 12px 24px -6px ${theme.palette.brandColors.blueberry.bold}`,
                                        }
                                    }}
                                >
                                    {t('pages.landingPage.hero.cta')}
                                </Button>
                            </Box>
                        </Grid>

                        {/* Visual Asset */}
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: { xs: 300, md: 450 },
                                    borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    background: `linear-gradient(135deg, ${theme.palette.brandColors.lavender.regular}, ${theme.palette.brandColors.blueberry.light})`,
                                    boxShadow: `
                                        inset 0 0 40px rgba(0,0,0,0.05),
                                        0 30px 60px -10px rgba(9, 30, 66, 0.15)
                                    `,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px solid rgba(255,255,255,0.4)`
                                }}
                            >
                                <video
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    style={{
                                        position: 'absolute',
                                        width: '115%',
                                        height: '115%',
                                        objectFit: 'cover',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        zIndex: 0,
                                        opacity: 0.95,
                                    }}
                                >
                                    <source src="/intro-training-space.mp4" type="video/mp4" />
                                </video>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: `radial-gradient(circle, transparent 40%, ${theme.palette.brandColors.lavender.regular} 150%)`,
                                        opacity: 0.4,
                                        pointerEvents: 'none',
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Box sx={{
                backgroundColor: 'white',
                py: { xs: 8, md: 12 },
                borderTopLeftRadius: { xs: 40, md: 100 },
                boxShadow: theme.mainShadows?.surface || 'none'
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        <FeatureCard
                            icon={<GroupWorkIcon fontSize="large" />}
                            color={theme.palette.brandColors.blueberry.bold}
                            title={t('pages.landingPage.features.spatialAudio.title')}
                            description={t('pages.landingPage.features.spatialAudio.description')}
                        />
                        <FeatureCard
                            icon={<BrushIcon fontSize="large" />}
                            color={theme.palette.brandColors.lavender.bold}
                            title={t('pages.landingPage.features.interactiveTools.title')}
                            description={t('pages.landingPage.features.interactiveTools.description')}
                        />
                        <FeatureCard
                            icon={<DashboardCustomizeIcon fontSize="large" />}
                            color={theme.palette.brandColors.oregano.bold}
                            title={t('pages.landingPage.features.customizable.title')}
                            description={t('pages.landingPage.features.customizable.description')}
                        />
                    </Grid>
                </Container>
            </Box>

            <Box component="footer" sx={{
                py: 4,
                textAlign: 'center',
                color: theme.palette.brandColors.slate.ink,
                fontSize: '0.9rem',
                backgroundColor: 'white'
            }}>
                <Typography variant="caption">
                     {t('pages.landingPage.footer.copyright')}
                </Typography>
            </Box>
        </Box>
    );
};

const FeatureCard: React.FC<{ icon: React.ReactNode, color: string, title: string, description: string }> = ({ icon, color, title, description }) => {
    const theme = useTheme();
    return (
        <Grid item xs={12} md={4}>
            <Box sx={{
                p: 4,
                borderRadius: 6,
                height: '100%',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: `1px solid ${theme.palette.brandColors.slate.light}`,
                backgroundColor: 'white',
                '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px -10px rgba(0,0,0,0.1)',
                    borderColor: 'transparent',
                }
            }}>
                <Box sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    color: 'white',
                    backgroundColor: color,
                    boxShadow: `0 10px 20px -5px ${color}80` // colored shadow
                }}>
                    {icon}
                </Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                    {title}
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ lineHeight: 1.6 }}>
                    {description}
                </Typography>
            </Box>
        </Grid>
    );
};
